import { ServiceLayout } from "@/components/service/ServiceLayout";
import { ServiceFAQ } from "@/components/service/ServiceFAQ";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { motion } from "framer-motion";
import { Search, BarChart, Globe, Trophy, Gauge, Users } from "lucide-react";
import { ServiceCTA } from "@/components/ui/ServiceCTA";
import { serviceFAQs } from "@/data/service-faqs";
import { Link } from "react-router-dom";

const SEO = () => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  const services = [
    {
      icon: <Search className="h-8 w-8 text-blue-500" />,
      title: "Technical SEO",
      description: "Optimize your website's structure, speed, and technical elements for better search engine visibility."
    },
    {
      icon: <BarChart className="h-8 w-8 text-blue-500" />,
      title: "Content Strategy",
      description: "Create engaging, keyword-optimized content that ranks well and converts visitors."
    },
    {
      icon: <Globe className="h-8 w-8 text-blue-500" />,
      title: "Local SEO",
      description: "Dominate local search results and attract more customers from your target area."
    },
    {
      icon: <Trophy className="h-8 w-8 text-blue-500" />,
      title: "Competitive Analysis",
      description: "Stay ahead of competitors with in-depth analysis and strategic recommendations."
    },
    {
      icon: <Gauge className="h-8 w-8 text-blue-500" />,
      title: "Performance Tracking",
      description: "Monitor your SEO progress with detailed analytics and regular automated reporting through AI agents."
    },
    {
      icon: <Users className="h-8 w-8 text-blue-500" />,
      title: "Link Building",
      description: "Build high-quality backlinks to increase your website's authority and rankings."
    }
  ];

  return (
    <ServiceLayout serviceName="SEO">
      <section 
        className="relative py-24 px-4"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-indigo-900/90" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-3xl">
            <motion.h1 
              className="text-5xl font-bold text-white mb-6"
              {...fadeIn}
            >
              Elevate Your Online Presence with SEO
            </motion.h1>
            <motion.p 
              className="text-xl text-gray-200 mb-8"
              {...fadeIn}
              transition={{ delay: 0.2 }}
            >
              Drive organic traffic, increase visibility, and achieve sustainable growth 
              with our data-driven SEO strategies tailored to your business goals. By leveraging AI agents 
              and content automation, we streamline and optimize your SEO efforts, ensuring faster results 
              and scalable performance, so your business stays ahead of the competition.
            </motion.p>
            <motion.div 
              {...fadeIn}
              transition={{ delay: 0.4 }}
            >
              <Link to="/demo">
                <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50">
                  Get Your Free SEO Audit
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-gradient-to-b from-white via-blue-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-4xl font-bold text-blue-900 mb-4">
              Comprehensive SEO Solutions
            </h2>
            <p className="text-xl text-blue-600 max-w-3xl mx-auto">
              Our expert team delivers results-driven SEO services to help your business 
              thrive in the digital landscape.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="group hover:shadow-2xl transition-all duration-300 border-none bg-gradient-to-br from-white to-blue-50">
                  <CardContent className="p-8">
                    <div className="mb-6 transform group-hover:scale-110 transition-transform duration-300">
                      {service.icon}
                    </div>
                    <h3 className="text-2xl font-semibold mb-4 text-blue-900 group-hover:text-blue-700 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-blue-600">{service.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <ServiceFAQ faqs={serviceFAQs["seo"]} serviceName="SEO" />
      <ServiceCTA serviceName="SEO" />
    </ServiceLayout>
  );
};

export default SEO;